import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Upload, Loader2, Download, Clock } from 'lucide-react';
import { downloadBlob } from './PaymentsImportPanel';
import TeacherHomework from './TeacherHomework';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (d) => (d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }) : '-');

export const downloadSubmissionFile = async (subId, file) => {
  try {
    const res = await axios.get(`${API}/homework/submissions/${subId}/files/${file.id}`, { responseType: 'blob' });
    downloadBlob(res.data, file.filename);
  } catch (e) {
    toast.error('Téléchargement impossible');
  }
};

export const SubmissionBadge = ({ sub, testId }) => {
  if (!sub) return <Badge className="bg-gray-100 text-gray-700" data-testid={testId}>Non rendu</Badge>;
  return sub.is_late
    ? <Badge className="bg-amber-100 text-amber-800" data-testid={testId}>Rendu en retard</Badge>
    : <Badge className="bg-green-100 text-green-800" data-testid={testId}>Rendu à temps</Badge>;
};

const SubmitForm = ({ hw, onDone }) => {
  const [files, setFiles] = useState([]);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [inputKey, setInputKey] = useState(0);
  const blocked = hw.is_past_deadline && !hw.accept_late;

  const send = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      if (comment) fd.append('comment', comment);
      const { data } = await axios.post(`${API}/homework/${hw.id}/submit`, fd);
      toast.success(data.message);
      setFiles([]); setComment(''); setInputKey((k) => k + 1);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dépôt impossible');
    } finally {
      setBusy(false);
    }
  };

  if (blocked) return <p className="text-sm text-red-700" data-testid={`homework-closed-${hw.id}`}>Date limite dépassée : ce devoir n’accepte pas les rendus en retard.</p>;
  return (
    <div className="space-y-2">
      {hw.is_past_deadline && <p className="text-sm text-amber-700">La date limite est passée : votre rendu sera signalé « en retard ».</p>}
      <Input key={inputKey} type="file" multiple accept=".pdf,.doc,.docx,.odt,.txt,.png,.jpg,.jpeg,.ppt,.pptx,.xls,.xlsx"
        onChange={(e) => setFiles(Array.from(e.target.files).slice(0, 3))} data-testid={`homework-files-${hw.id}`} />
      <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Commentaire pour le professeur (facultatif)" data-testid={`homework-comment-${hw.id}`} />
      <Button size="sm" onClick={send} disabled={busy || files.length === 0} data-testid={`homework-submit-${hw.id}`}>
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
        {hw.submission ? 'Remplacer mon rendu' : 'Déposer mon devoir'}
      </Button>
      <p className="text-xs text-gray-500">1 à 3 fichiers, 10 Mo maximum chacun.</p>
    </div>
  );
};

const HomeworkCard = ({ hw, canSubmit, onDone }) => (
  <Card data-testid={hw.child_name ? `homework-card-${hw.id}-${hw.student_id}` : `homework-card-${hw.id}`}>
    <CardContent className="pt-6 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-semibold text-lg">{hw.title}</h3>
        <span className="text-gray-500">· {hw.subject_name}</span>
        {hw.child_name && !canSubmit && <Badge className="bg-slate-100 text-slate-700">{hw.child_name}</Badge>}
        <SubmissionBadge sub={hw.submission} testId={`homework-status-${hw.id}`} />
        <Badge className={hw.accept_late ? 'bg-sky-100 text-sky-800' : 'bg-rose-100 text-rose-800'}>
          {hw.accept_late ? 'Retard accepté (signalé)' : 'Aucun retard accepté'}
        </Badge>
      </div>
      <p className="text-sm text-gray-600 flex items-center gap-1"><Clock className="w-4 h-4" /> À rendre avant le {fmt(hw.deadline)}</p>
      {(hw.instructions || hw.description) && <p className="text-sm text-gray-700">{hw.instructions || hw.description}</p>}
      {hw.submission && (
        <div className="text-sm bg-gray-50 rounded-md p-3 space-y-1">
          <p>Déposé le {fmt(hw.submission.submitted_at)}{hw.submission.attempts > 1 ? ` (dépôt n° ${hw.submission.attempts})` : ''}</p>
          {hw.submission.files.map((f) => (
            <button key={f.id} type="button" className="text-blue-700 hover:underline flex items-center gap-1" onClick={() => downloadSubmissionFile(hw.submission.id, f)}>
              <Download className="w-3 h-3" /> {f.filename}
            </button>
          ))}
        </div>
      )}
      {hw.submission?.feedback && (
        <div className="text-sm rounded-md p-3 bg-blue-50 border border-blue-200" data-testid={`homework-feedback-${hw.id}`}>
          <p className="font-medium">
            Corrigé par {hw.submission.feedback.graded_by_name}
            {hw.submission.feedback.grade != null && ` · ${hw.submission.feedback.grade}/${hw.submission.feedback.max_grade}`}
            {hw.submission.feedback.grade != null && (hw.submission.feedback.count_in_average ? ' (comptée dans la moyenne)' : ' (note indicative)')}
          </p>
          {hw.submission.feedback.comment && <p className="text-gray-700 mt-1">« {hw.submission.feedback.comment} »</p>}
        </div>
      )}
      {canSubmit && <SubmitForm hw={hw} onDone={onDone} />}
    </CardContent>
  </Card>
);

export default function HomeworkPage({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const isLearner = ['STUDENT', 'PARENT'].includes(user.role);
  const load = () => axios.get(`${API}/homework/my`).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { if (isLearner) load(); }, []);

  if (!isLearner) return <TeacherHomework user={user} />;
  return (
    <div className="p-6 space-y-4" data-testid="homework-page">
      <h1 className="text-2xl font-bold">{user.role === 'PARENT' ? 'Devoirs de mes enfants' : 'Mes devoirs'}</h1>
      {!loading && items.length === 0 && <p className="text-gray-500">Aucun devoir à rendre pour le moment.</p>}
      {items.map((hw) => <HomeworkCard key={`${hw.id}-${hw.student_id}`} hw={hw} canSubmit={user.role === 'STUDENT'} onDone={load} />)}
    </div>
  );
}
